import React, { FC, useState, useEffect } from 'react'
import clsx from 'clsx'
import { useMutation, useQueryClient } from 'react-query'
import axios from 'axios'
import { successToast, errorToast } from '../../../../../../../_metronic/helpers/components/Toaster'
import { QUERIES } from '../../../../../../../_metronic/helpers'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useFormik } from 'formik'
import * as Yup from 'yup'

type Props = {
    id: any
    close: () => void
}

const VerifyModal: FC<Props> = ({ id, close }) => {
    const { query } = useQueryResponse()
    const queryClient = useQueryClient()
    const [loading, setLoading] = useState(false)
    const [deliveryTerms, setDeliveryTerms] = useState<any[]>([])

    useEffect(() => {
        fetchDeliveryTerms()
    }, [])

    const fetchDeliveryTerms = async () => {
        try {
            const authData = localStorage.getItem('kt-auth-react-v')
            const token = authData ? JSON.parse(authData).data.token : null

            const response = await axios.get(
                'https://pfc-backend-api.asmglobal.co.in/v1/api/deliveryterms?page=0&size=10',
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            if (response.data && response.data.data) {
                setDeliveryTerms(response.data.data)
            }
        } catch (error) {
            console.error('Error fetching delivery terms:', error)
        }
    }
    const validationSchema = Yup.object().shape({
        purchasePrice: Yup.string().required('Purchase Price is required'),
        purchaseQuantity: Yup.string().required('Purchase Quantity is required'),
        paymentTerms: Yup.string().required('Payment Terms is required'),
    })

    const formik = useFormik({
        initialValues: {
            purchasePrice: id?.purchasePriceInfo?.pricePerUnit || "",
            purchaseQuantity: id?.quantityInfo?.quantity || "",
            paymentTerms: id?.purchasePriceInfo?.paymentTerms || "",
        },
        validationSchema,
        onSubmit: handleApprove,
    })

    async function handleApprove() {
        try {
            setLoading(true)

            // Get auth token and user info from localStorage
            const authData = localStorage.getItem('kt-auth-react-v')
            const token = authData ? JSON.parse(authData).data.token : null
            const userId = authData ? JSON.parse(authData).data.userId : 'F2001'
            const userName = authData ? JSON.parse(authData).data.userName : 'FinanceUser'

            console.log('Using userId:', userId)
            console.log('Using userName:', userName)

            // Make API call to approve order
            const response = await axios.post(
                `https://pfc-backend-api.asmglobal.co.in/v1/api/transactions/${id.id}/verify`,
                {
                    saleOrderId: id?.sellerInfo?.sellerId,
                    pricePerUnit: formik.values.purchasePrice,
                    quantity: formik.values.purchaseQuantity,
                    totalAmount: +formik.values.purchaseQuantity * +formik.values.purchasePrice,
                    paymentTerms: formik.values.paymentTerms,
                },
                {
                    params: {
                        userId: userId,
                        userName: userName,
                    },
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            )

            console.log('Approval Response:', response.data)
            successToast('Order approved successfully!')
            queryClient.invalidateQueries([`${QUERIES.USERS_LIST}-${query}`])
            close()
        } catch (error) {
            console.error('Error approving order:', error)
            if (error instanceof Error) {
                errorToast(error.message)
            } else {
                errorToast('Error approving order')
            }
        } finally {
            setLoading(false)
        }
    }

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 2000,
            }}
            onClick={close}
        >
            <div
                style={{
                    backgroundColor: 'white',
                    padding: '30px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    maxWidth: '500px',
                    width: '90%',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <h3 style={{ marginBottom: '20px', fontWeight: 600, fontSize: "17.55px" }}>Verify Order</h3>

                <form onSubmit={formik.handleSubmit} noValidate>
                    {/* Purchase Price Field */}
                    <div className='fv-row mb-7'>
                        <label className='required fw-bold fs-6 mb-2'>Purchase Price (INR/kg)</label>
                        <input
                            type='text'
                            {...formik.getFieldProps('purchasePrice')}
                            placeholder='Enter purchase price'
                            className='form-control form-control-solid mb-3 mb-lg-0'
                            autoComplete='off'
                            disabled={loading}
                        />
                        {formik.touched.purchasePrice && formik.errors.purchasePrice && (
                            <div className='fv-plugins-message-container'>
                                <div className='fv-help-block'>
                                    <span role='alert'>{formik.errors.purchasePrice as string}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Purchase Quantity Field */}
                    <div className='fv-row mb-7'>
                        <label className='required fw-bold fs-6 mb-2'>Purchase Quantity (kgs)</label>
                        <input
                            type='text'
                            {...formik.getFieldProps('purchaseQuantity')}
                            placeholder='Enter purchase quantity'
                            className='form-control form-control-solid mb-3 mb-lg-0'
                            autoComplete='off'
                            disabled={loading}
                        />
                        {formik.touched.purchaseQuantity && formik.errors.purchaseQuantity && (
                            <div className='fv-plugins-message-container'>
                                <div className='fv-help-block'>
                                    <span role='alert'>{formik.errors.purchaseQuantity as string}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Payment Terms Field */}
                    <div className='fv-row mb-7'>
                        <label className='required fw-bold fs-6 mb-2'>Payment Terms</label>
                        <select
                            style={{ background: 'white', border: '1px solid #cccccc' }}
                            {...formik.getFieldProps('paymentTerms')}
                            className={clsx('form-control form-control-solid', {
                                'is-invalid': formik.touched.paymentTerms && formik.errors.paymentTerms,
                            })}
                            disabled={loading}
                        >
                            <option value=''>Select Payment Term</option>
                            {deliveryTerms.map((term: any) => (
                                <option key={term.id} value={term.meaning}>
                                    {term.deliveryCode} - {term.meaning}
                                </option>
                            ))}
                        </select>
                        {formik.touched.paymentTerms && formik.errors.paymentTerms && (
                            <div className='fv-plugins-message-container'>
                                <div className='fv-help-block'>
                                    <span role='alert'>{formik.errors.paymentTerms as string}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                        <button
                            type='button'
                            onClick={close}
                            className='btn btn-light'
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type='submit'
                            className='btn btn-success'
                            disabled={loading}
                        >
                            {loading ? 'Approving...' : 'Approve'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default VerifyModal